// src/components/about/About.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Accordion, Badge } from 'react-bootstrap';
import { pageService } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBullseye, 
    faEye, 
    faHistory, 
    faCheckCircle,
    faNewspaper,
    faShieldAlt,
    faUsers,
    faHandsHelping,
    faHeart,
    faBookOpen,
    faAward,
    faGlobe,
    faUserGraduate,
    faHome,
    faHospital,
    faUtensils,
    faDumbbell,
    faMusic,
    faBriefcase,
    faBalanceScale,
    faBuilding
} from '@fortawesome/free-solid-svg-icons';

const About = () => {
    const [aboutData, setAboutData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAboutData();
    }, []);

    const fetchAboutData = async () => {
        try {
            const response = await pageService.getAbout();
            setAboutData(response.data.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching about data:', error);
            // Fallback data with new content
            setAboutData({
                title: 'Indian Newspaper & Media Association',
                subtitle: 'Empowering Journalism. Protecting Press Freedom. Serving Society.',
                description: 'The Indian Newspaper & Media Association (INMA) was established in September 2014 with the vision of creating a strong, responsible and united platform for journalists, newspaper professionals and media practitioners across India. The Association is committed to protecting the legitimate interests, welfare and professional rights of journalists while upholding the fundamental principles of press freedom, professional integrity, ethical journalism and freedom of expression.',
                mission: 'The Indian Newspaper & Media Association strives to protect the rights and interests of journalists through lawful and legitimate activities, safeguard press freedom and freedom of expression, promote the highest standards of journalism and professional ethics, improve working conditions of journalists and media professionals, promote journalists\' welfare through legal assistance, welfare and relief initiatives, and support the families and dependents of members.',
                vision: 'To build a strong, independent, ethical and socially responsible media community in India, where journalists are respected, protected and provided with opportunities for professional growth and welfare.',
                objectives: [
                    'Protect the rights and interests of journalists through lawful and legitimate activities',
                    'Safeguard press freedom and freedom of expression',
                    'Promote the highest standards of journalism, professional ethics and integrity',
                    'Improve working conditions of journalists and media professionals',
                    'Promote journalists\' welfare through legal assistance and relief initiatives',
                    'Support the families and dependents of members',
                    'Organise seminars, conferences, workshops and symposia',
                    'Promote journalism education and training programmes',
                    'Collaborate with professional organisations and media institutions',
                    'Recognise excellence in journalism through awards and honours',
                    'Promote fellowships, research and advanced studies in journalism',
                    'Encourage intellectual, cultural and sports activities among members'
                ],
                welfare: [
                    'Indoor and outdoor sports facilities',
                    'Cultural programmes and social gatherings',
                    'Picnics, tours and recreational activities',
                    'Literary and educational programmes',
                    'Library and reading facilities',
                    'Debates, discussions and knowledge-sharing programmes',
                    'Gymnasium and fitness activities',
                    'Canteen and other member amenities',
                    'Guest accommodation and recreational facilities',
                    'Health awareness programmes',
                    'Yoga and wellness programmes',
                    'Medical and health camps for needy communities'
                ],
                commitment: 'For Press Freedom. For Journalists. For Professional Excellence. For Welfare. For Society. For Democracy.'
            });
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <section className="py-5 bg-light">
            <Container>
                {/* Hero Section */}
                <Row className="mb-5">
                    <Col className="text-center">
                        <h1 className="display-3 fw-bold text-primary mb-3">{aboutData.title}</h1>
                        <div className="divider mx-auto" style={{ width: '80px', height: '4px', background: 'linear-gradient(90deg, #0056b3, #007bff)', marginBottom: '1.5rem' }} />
                        <h3 className="fw-bold text-dark mb-3">{aboutData.subtitle}</h3>
                        <p className="lead text-muted mx-auto fw-medium" style={{ maxWidth: '800px', textAlign: 'justify' }}>{aboutData.description}</p>
                    </Col>
                </Row>

                {/* Mission & Vision Section */}
                <Row className="mb-5">
                    <Col md={6} className="mb-4">
                        <Card className="h-100 shadow-lg border-0 bg-primary text-white p-4">
                            <Card.Body>
                                <div className="mb-3">
                                    <FontAwesomeIcon icon={faBullseye} size="2x" />
                                </div>
                                <h3 className="fw-bold mb-3">Our Mission</h3>
                                <p className="mb-0" style={{ fontSize: '1.05rem', lineHeight: '1.8' }}>{aboutData.mission}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6} className="mb-4">
                        <Card className="h-100 shadow-lg border-0 bg-dark text-white p-4">
                            <Card.Body>
                                <div className="mb-3">
                                    <FontAwesomeIcon icon={faEye} size="2x" />
                                </div>
                                <h3 className="fw-bold mb-3">Our Vision</h3>
                                <p className="mb-0" style={{ fontSize: '1.05rem', lineHeight: '1.8' }}>{aboutData.vision}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Objectives Section */}
                <Row className="mb-5">
                    <Col>
                        <Card className="shadow-lg border-0 p-4">
                            <Card.Body>
                                <h3 className="fw-bold mb-4 text-primary">
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                    Our Objectives
                                </h3>
                                <Row>
                                    {aboutData.objectives.map((objective, index) => (
                                        <Col md={6} key={index} className="mb-3">
                                            <div className="d-flex align-items-start p-2 rounded-3" style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'transparent' }}>
                                                <span className="text-primary me-2" style={{ fontSize: '1.2rem' }}>✦</span>
                                                <p className="mb-0 fw-medium">{objective}</p>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Welfare & Community Development */}
                <Row className="mb-5">
                    <Col>
                        <Card className="shadow-lg border-0 p-4 bg-primary text-white">
                            <Card.Body>
                                <h3 className="fw-bold mb-4">
                                    <FontAwesomeIcon icon={faHandsHelping} className="me-2" />
                                    Welfare & Community Development
                                </h3>
                                <p className="mb-4" style={{ fontSize: '1.05rem' }}>
                                    INMA believes that the welfare of journalists must extend beyond their professional lives. 
                                    The Association seeks to develop various social, recreational, educational and welfare facilities for its members.
                                </p>
                                <Row>
                                    {aboutData.welfare.map((item, index) => (
                                        <Col md={4} key={index} className="mb-3">
                                            <div className="d-flex align-items-start">
                                                <span className="text-warning me-2">✓</span>
                                                <span className="fw-medium">{item}</span>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Additional Sections - Accordion Style */}
                <Row className="mb-5">
                    <Col>
                        <Card className="shadow-lg border-0 p-4">
                            <Card.Body>
                                <h3 className="fw-bold mb-4 text-primary">
                                    <FontAwesomeIcon icon={faBalanceScale} className="me-2" />
                                    Our Commitment to Society
                                </h3>
                                <Accordion defaultActiveKey="0" flush>
                                    <Accordion.Item eventKey="0" className="border-0 mb-3">
                                        <Accordion.Header className="bg-light rounded-3 fw-bold">
                                            <FontAwesomeIcon icon={faHeart} className="text-primary me-2" />
                                            Health & Social Responsibility
                                        </Accordion.Header>
                                        <Accordion.Body className="bg-white">
                                            <p className="mb-0">
                                                As part of its commitment to society, the Association seeks to conduct health-awareness programmes, 
                                                medical camps and yoga and wellness initiatives for poor and needy people in both rural and urban areas. 
                                                INMA also seeks to support wider socio-economic and community development activities, including donations 
                                                and assistance to deserving social-service organisations.
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey="1" className="border-0 mb-3">
                                        <Accordion.Header className="bg-light rounded-3 fw-bold">
                                            <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
                                            Support for Senior Citizens
                                        </Accordion.Header>
                                        <Accordion.Body className="bg-white">
                                            <p className="mb-0">
                                                The Association aims to contribute to the welfare and dignity of senior citizens by supporting 
                                                initiatives such as old-age homes and senior-care programmes, subject to the resources and programmes 
                                                undertaken by the Association from time to time.
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey="2" className="border-0 mb-3">
                                        <Accordion.Header className="bg-light rounded-3 fw-bold">
                                            <FontAwesomeIcon icon={faUsers} className="text-primary me-2" />
                                            Member Welfare & Relief
                                        </Accordion.Header>
                                        <Accordion.Body className="bg-white">
                                            <p className="mb-0">
                                                The Association recognises the importance of standing beside members and their families during 
                                                difficult times. It seeks to establish appropriate relief and welfare mechanisms, including death-relief 
                                                assistance for the families of eligible members, subject to the applicable rules and availability of funds.
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey="3" className="border-0 mb-3">
                                        <Accordion.Header className="bg-light rounded-3 fw-bold">
                                            <FontAwesomeIcon icon={faBuilding} className="text-primary me-2" />
                                            Member Amenities & Development
                                        </Accordion.Header>
                                        <Accordion.Body className="bg-white">
                                            <Row>
                                                <Col md={4} className="mb-2">
                                                    <span className="fw-medium">✦ Housing-related initiatives</span>
                                                </Col>
                                                <Col md={4} className="mb-2">
                                                    <span className="fw-medium">✦ Consumer welfare programmes</span>
                                                </Col>
                                                <Col md={4} className="mb-2">
                                                    <span className="fw-medium">✦ Member assistance schemes</span>
                                                </Col>
                                                <Col md={4} className="mb-2">
                                                    <span className="fw-medium">✦ Recreational and sports facilities</span>
                                                </Col>
                                                <Col md={4} className="mb-2">
                                                    <span className="fw-medium">✦ Health and fitness facilities</span>
                                                </Col>
                                                <Col md={4} className="mb-2">
                                                    <span className="fw-medium">✦ Guest rooms and hospitality facilities</span>
                                                </Col>
                                                <Col md={4} className="mb-2">
                                                    <span className="fw-medium">✦ Restaurants and canteen services</span>
                                                </Col>
                                                <Col md={4} className="mb-2">
                                                    <span className="fw-medium">✦ Libraries and information centres</span>
                                                </Col>
                                            </Row>
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey="4" className="border-0">
                                        <Accordion.Header className="bg-light rounded-3 fw-bold">
                                            <FontAwesomeIcon icon={faBookOpen} className="text-primary me-2" />
                                            Information & Knowledge Centre
                                        </Accordion.Header>
                                        <Accordion.Body className="bg-white">
                                            <p className="mb-0">
                                                The Association also seeks to function as an information and knowledge bureau for journalists and 
                                                media professionals by facilitating access to relevant information, professional resources, training 
                                                opportunities, research material and developments concerning the media industry.
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Journalism with Responsibility */}
                <Row className="mb-5">
                    <Col>
                        <Card className="shadow-lg border-0 p-4 bg-dark text-white">
                            <Card.Body className="text-center">
                                <h3 className="fw-bold mb-3">
                                    <FontAwesomeIcon icon={faNewspaper} className="me-2 text-warning" />
                                    Journalism with Responsibility
                                </h3>
                                <p className="mb-3" style={{ fontSize: '1.1rem' }}>
                                    The Indian Newspaper & Media Association believes that press freedom and professional responsibility must go hand in hand.
                                </p>
                                <p className="mb-4">
                                    A strong media fraternity is one that protects its members, respects ethical journalism, encourages professional excellence 
                                    and remains committed to truth, accountability and public interest.
                                </p>
                                <p className="mb-0 fw-bold text-warning" style={{ fontSize: '1.2rem' }}>
                                    Through collective effort, professional cooperation and social responsibility, INMA seeks to contribute to the growth 
                                    of a free, responsible, ethical and vibrant Indian media.
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Commitment Section */}
                <Row>
                    <Col>
                        <Card className="shadow-lg border-0 p-4 text-center" style={{ background: 'linear-gradient(135deg, #0056b3, #007bff)' }}>
                            <Card.Body className="text-white">
                                <h2 className="fw-bold mb-3">Our Commitment</h2>
                                <div className="d-flex flex-wrap justify-content-center gap-4">
                                    <Badge bg="light" text="primary" className="p-3 fs-6 fw-bold rounded-pill">
                                        Press Freedom
                                    </Badge>
                                    <Badge bg="light" text="primary" className="p-3 fs-6 fw-bold rounded-pill">
                                        For Journalists
                                    </Badge>
                                    <Badge bg="light" text="primary" className="p-3 fs-6 fw-bold rounded-pill">
                                        Professional Excellence
                                    </Badge>
                                    <Badge bg="light" text="primary" className="p-3 fs-6 fw-bold rounded-pill">
                                        For Welfare
                                    </Badge>
                                    <Badge bg="light" text="primary" className="p-3 fs-6 fw-bold rounded-pill">
                                        For Society
                                    </Badge>
                                    <Badge bg="light" text="primary" className="p-3 fs-6 fw-bold rounded-pill">
                                        For Democracy
                                    </Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export default About;